import React, { useRef } from "react";
import dayjs from "dayjs";
import ReactECharts from "echarts-for-react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useGetTotalInstalledCapacityQuery } from "../OperatorDashboard/operatorDashboardAPISlice";
import { darkBg, gridColor, labelColor, makeGradient, tooltipStyle } from "../api/echartsTheme";
import GraphDownloadButton from "../../Global/other/GraphDownloadButton";
import { formatSAST } from "../../Global/other/formatSAST";

const color = "#FDB940";

export default function TjommieDemandGraph({ graph_data }) {
  const chartRef = useRef(null);
  const {
    data: totalInstalledCapacity,
    isLoading: capacityLoading,
  } = useGetTotalInstalledCapacityQuery();

  if (!graph_data) {
    return (
      <Grid item sx={{ p: 2 }}>
        <CircularProgress size={20} sx={{ color }} />
      </Grid>
    );
  }

  const seriesData = (graph_data.timestamps || []).map((ts, i) => [
    new Date(ts).getTime(),
    (graph_data.tjommie_data || [])[i] ?? null,
  ]);

  const option = {
    backgroundColor: darkBg,
    animation: false,
    grid: { top: 16, right: 24, bottom: 76, left: 56 },
    dataZoom: [
      { type: "inside", xAxisIndex: 0, filterMode: "filter" },
      {
        type: "slider",
        xAxisIndex: 0,
        height: 18,
        bottom: 32,
        backgroundColor: "rgba(255,255,255,0.03)",
        borderColor: "rgba(255,255,255,0.07)",
        fillerColor: "rgba(253,185,64,0.2)",
        handleStyle: { color: "#FDB940", borderColor: "#FDB940" },
        moveHandleStyle: { color: "#FDB940" },
        emphasis: { handleStyle: { color: "#FDB940" } },
        textStyle: { color: "transparent" },
        brushSelect: false,
        showDetail: false,
      },
    ],
    tooltip: {
      trigger: "axis",
      ...tooltipStyle,
      formatter: (params) =>
        params
          .filter((p) => p.value !== null)
          .map((p) => `${p.seriesName}: <b>${p.value[1]}</b>`)
          .join("<br/>"),
    },
    xAxis: {
      type: "time",
      axisLine: { lineStyle: { color: gridColor } },
      axisTick: { lineStyle: { color: gridColor } },
      axisLabel: {
        color: labelColor,
        fontSize: 10,
        formatter: (v) => {
          const d = new Date(v);
          return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
        },
      },
      splitLine: { show: false },
    },
    legend: {
      bottom: 4,
      left: 16,
      backgroundColor: "rgba(255,255,255,0.06)",
      borderRadius: 20,
      padding: [5, 14],
      textStyle: { color: "#e0e6f0", fontSize: 11 },
      itemWidth: 16,
      itemHeight: 3,
    },
    yAxis: {
      type: "value",
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: labelColor, fontSize: 10 },
      splitLine: { lineStyle: { color: gridColor, type: "dashed" } },
    },
    series: [
      {
        name: "Tjommie Demand",
        type: "line",
        smooth: false,
        symbol: "none",
        lineStyle: { color, width: 2 },
        areaStyle: { color: makeGradient(color), opacity: 0.25 },
        itemStyle: { color },
        data: seriesData.filter(([, y]) => y !== null),
      },
    ],
  };

  const handleDownloadCsv = () => {
    if (!graph_data?.timestamps) return;
    let csv = "Timestamp (SAST),Tjommie Demand\r\n";
    graph_data.timestamps.forEach((ts, i) => {
      csv += `${formatSAST(ts)},${graph_data.tjommie_data[i] ?? ""}\r\n`;
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Tjommie_Demand_${dayjs().format("YYYY-MM-DD")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPng = () => {
    const url = chartRef.current?.getEchartsInstance()?.getDataURL({ type: "png", pixelRatio: 2, backgroundColor: darkBg });
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `Tjommie_Demand_${dayjs().format("YYYY-MM-DD")}.png`;
    a.click();
  };

  return (
    <Card sx={{ borderRadius: "12px", backgroundColor: darkBg, border: "1px solid rgba(255,255,255,0.06)" }}>
      <Grid container alignItems="center" sx={{ pt: "20px", px: "20px", pb: "12px" }}>
        <Grid item xs>
          <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", letterSpacing: "-0.01em" }}>
            Tjommie Demand
          </Typography>
        </Grid>
        <Grid item sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Typography sx={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.55)" }}>
            Total Geyser Installed Capacity:{" "}
            {capacityLoading ? (
              <CircularProgress size={12} sx={{ color, verticalAlign: "middle" }} />
            ) : (
              <Box component="span" sx={{ color: "#FDB940", fontWeight: 700 }}>
                {totalInstalledCapacity !== undefined ? `${totalInstalledCapacity} MVA` : "—"}
              </Box>
            )}
          </Typography>
          <GraphDownloadButton onDownloadCsv={handleDownloadCsv} onDownloadPng={handleDownloadPng} />
        </Grid>
      </Grid>
      <Grid item sx={{ width: "100%", px: "12px", pb: "12px" }}>
        <ReactECharts ref={chartRef} option={option} style={{ height: 380 }} notMerge />
      </Grid>
    </Card>
  );
}
